// R-162 §11.2 — tiny inline-SVG sparkline for the FeedExhaustedPage session
// debrief. No charting dependency: one <polyline> normalized to the viewport.
// Daily-aggregate series (e.g. avg-time-per-field or accuracy over the last
// N days). Empty data renders a muted "No data yet" placeholder.

import styles from "./MiniSparkline.module.css";

interface MiniSparklineProps {
  data: number[];
  /** Accessible label describing what the trend represents. */
  ariaLabel: string;
  width?: number;
  height?: number;
}

export function MiniSparkline({
  data,
  ariaLabel,
  width = 100,
  height = 30,
}: MiniSparklineProps) {
  if (data.length === 0) {
    return (
      <span className={styles.empty} role="img" aria-label={ariaLabel}>
        No data yet
      </span>
    );
  }

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min;
  // Horizontal span: a single point sits at x=0; N points span the full width.
  const step = data.length > 1 ? width / (data.length - 1) : 0;
  // Inset so the stroke isn't clipped at the top/bottom edges.
  const pad = 2;
  const usableHeight = height - pad * 2;

  const points = data
    .map((value, i) => {
      const x = i * step;
      // Higher value → higher on screen (smaller y). Flat series → centered.
      const ratio = range === 0 ? 0.5 : (value - min) / range;
      const y = pad + (1 - ratio) * usableHeight;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      className={styles.sparkline}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--color-accent, #ea580c)"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
